import type { Channel } from '@/Enums/Channel.enum';
import PackageDeliveredIcon from '@/components/Icons/PackageDeliveredIcon';
import SearchIcon from '@/components/Icons/SearchIcon';
import StyledInput from '@/components/TabSelect/styled/StyledInput';
import { GREY } from '@/constants/color';
import { RON_PRODUCT } from '@/constants/forecaster';
import { useGamSubAdUnits, useGamTopAdUnits } from '@/hooks/forecaster/useGamInventory';
import useListSearch from '@/hooks/useListSearch';
import { useAppSelector } from '@/store/hooks';
import { selectHasGamAccount } from '@/store/selectors/currentWhitelabel.selectors';
import { Checkbox, FormControlLabel, Paper, Radio, RadioGroup } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface IProps {
  currentChannel: Channel;
}

const columnHeader = `flex-1 px-4 py-2 font-[Manrope] text-[${GREY}] text-[14px] font-semibold`;

const GamProductSelection = ({ currentChannel }: IProps) => {
  const { control, setValue } = useFormContext();

  const topField = `${currentChannel}.gamTopAdUnitId`;
  const subField = `${currentChannel}.gamSubAdUnitIds`;

  const selectedTop: string = useWatch({ control, name: topField }) ?? RON_PRODUCT.id;
  const selectedSubs: string[] = useWatch({ control, name: subField }) ?? [];

  const isRon = selectedTop === RON_PRODUCT.id;

  // Gate fetches on the GAM account so a direct URL can't hit gated endpoints.
  const hasGamAccount = useAppSelector(selectHasGamAccount);
  const { adUnits: topAdUnits, isLoading: loadingTop } = useGamTopAdUnits(hasGamAccount);
  const { adUnits: subAdUnits, isLoading: loadingSub } = useGamSubAdUnits(hasGamAccount && !isRon ? selectedTop : null);

  const topSearch = useListSearch();
  const subSearch = useListSearch();

  const displayedTop = useMemo(() => {
    if (!topSearch.searchTerm.length) return topAdUnits;
    return topAdUnits.filter(u => u.name?.toLowerCase().includes(topSearch.searchTerm));
  }, [topAdUnits, topSearch.searchTerm]);

  const displayedSub = useMemo(() => {
    if (!subSearch.searchTerm.length) return subAdUnits;
    return subAdUnits.filter(u => u.name?.toLowerCase().includes(subSearch.searchTerm));
  }, [subAdUnits, subSearch.searchTerm]);

  const handleTopChange = useCallback(
    (id: string) => {
      setValue(topField, id);
      setValue(subField, []);
    },
    [setValue, topField, subField],
  );

  const toggleSub = useCallback(
    (id: string) => {
      const next = selectedSubs.includes(id) ? selectedSubs.filter(x => x !== id) : [...selectedSubs, id];
      setValue(subField, next);
    },
    [selectedSubs, setValue, subField],
  );

  return (
    <Paper className='col-span-3 p-4 print:p-[10px] break-after-page'>
      <div className='flex flex-col gap-3'>
        <div className='flex items-center'>
          <PackageDeliveredIcon />
          <p className='ml-2 text-[#2E2C34] text-[18px] font-semibold'>Product Selection</p>
        </div>
        <div className='flex flex-col border border-[#E8E8E8] rounded-md'>
          <div className='w-full flex bg-[#E8E8E8]/40 rounded-t-lg'>
            <p className={`${columnHeader} border-r`}>Inventory</p>
            <p className={columnHeader}>Ad Units</p>
          </div>
          <div className='flex max-h-72'>
            <div className='flex-1 p-2 overflow-y-auto border-r flex flex-col'>
              <StyledInput
                fullWidth
                startAdornment={<SearchIcon className={`text-[${GREY}]`} />}
                placeholder='Type to search'
                value={topSearch.search}
                onChange={topSearch.onSearchValueChange}
                sx={{ padding: '0 7px 0 16px', marginBottom: '4px' }}
              />
              <RadioGroup value={selectedTop} onChange={(_, value) => handleTopChange(value)}>
                <FormControlLabel
                  sx={{ marginLeft: 0, fontSize: '14px' }}
                  value={RON_PRODUCT.id}
                  control={<Radio sx={{ padding: '6px' }} />}
                  label='Run of Network'
                />
                {loadingTop && <p className='text-sm text-gray-400 px-2 py-1'>Loading…</p>}
                {displayedTop.map(unit => (
                  <FormControlLabel
                    key={unit.id}
                    sx={{ marginLeft: 0, fontSize: '14px' }}
                    value={unit.id}
                    control={<Radio sx={{ padding: '6px' }} />}
                    label={unit.name}
                  />
                ))}
              </RadioGroup>
            </div>
            <div className='flex-1 p-2 overflow-y-auto flex flex-col'>
              <StyledInput
                fullWidth
                startAdornment={<SearchIcon className={`text-[${GREY}]`} />}
                placeholder='Type to search'
                value={subSearch.search}
                onChange={subSearch.onSearchValueChange}
                sx={{ padding: '0 7px 0 16px', marginBottom: '4px' }}
              />
              {isRon && <p className='text-sm text-gray-400 px-2 py-1'>All network inventory (Run of Network)</p>}
              {!isRon && loadingSub && <p className='text-sm text-gray-400 px-2 py-1'>Loading…</p>}
              {!isRon && !loadingSub && !displayedSub.length && (
                <p className='text-sm text-gray-400 px-2 py-1'>No ad units</p>
              )}
              {!isRon &&
                displayedSub.map(unit => (
                  <FormControlLabel
                    key={unit.id}
                    sx={{ marginLeft: 0, fontSize: '14px' }}
                    control={
                      <Checkbox
                        sx={{ padding: '6px' }}
                        checked={selectedSubs.includes(unit.id)}
                        onChange={() => toggleSub(unit.id)}
                      />
                    }
                    label={unit.name}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
    </Paper>
  );
};

export default GamProductSelection;
